import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get, FieldValues, UseFormReturn } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { parse } from "qs";

import { Box, Button, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { Analytics as AnalyticsIcon } from "@mui/icons-material";

import { AutoSave, FormWrapper } from "@framework/mui-form";
import { calculateStartDate, RangeType, toFakeUtc, useAsu } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";
import { useApiCall } from "@framework/react-hooks";
import { useUser } from "@framework/provider-user";
import { IUser, UserVersion } from "@framework/types";

import { createEmptyAnalyticsResponse } from "./utils/emptyResponseFactory";
import {
  buildFaultFrequencyColorMap,
  createStableColorAssigner,
  filterValidFaultLineKeys,
  getFaultFrequencySelectionKey,
} from "./utils/line-colors";
import { useToFakeUtcISO } from "../../../../../../hooks";
import {
  transformCommonParamsToChartData,
  transformMeasuresDataMultiplePoints,
  transformMeasuresDataSinglePoint,
} from "./mappers/trendMappers";
import { transformSingleSignalToChart } from "./mappers/signalMappers";
import { transformAmplitudePhaseToOrbit, transformOrbitDataToChart } from "./mappers/orbitMappers";
import { mergeRecorderModeChartData, mergeRecorderModeTableDataMultiple } from "./recorder/mergeSeries";
import { trimChartToWindow, trimTableToWindow } from "./recorder/applyWindow";

import {
  measureTypeName,
  objectName,
  idObjectName,
  idObjectsOptionsName,
  idParameterName,
  idParametersOptionsName,
  idMeasureName,
  idMeasuresOptionsName,
} from "./constants";

import {
  IAnalyticsForm,
  type TCommonParamsResponse,
  IChartData,
  IGetCommonParamsDataValues,
  IGetCommonPointsDataValues,
  IGetPointMeasuresDataValues,
  IGetSignalDataValues,
  IGetOrbitDataValues,
  IMeasuresResponse,
  IOption,
  IPointResponse,
  ISignalDataResponse,
  IOrbitDataResponse,
  IChartDataSignal,
  IChartDataOrbit,
  ITrainDetailsRotationResponse,
  IComponentAutocompleteResponse,
  IComponentFaultFrequencyApi,
  IFaultFrequencyChartCursor,
  ANALYTICS_VIEW,
  ANALYTICS_MEASURE_TYPE,
  ANALYTICS_OBJECT,
  TRAIN_ROTATION_DIRECTION,
  IParameterOption,
  IObjectOption,
} from "./interfaces";
import { ITableData, PARAMETER_TYPE } from "./components/table/interfaces";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { DashboardAccordion } from "../../ui/accordion";
import { Selects } from "./components/selects";
import { AnalyticsView } from "./components/view";
import { AnalyticsViewSwitcher } from "./components/analytics-view-switcher";
import { AnalyticsExport } from "./components/analytics-export";
import { SingleSelectWithArrows } from "./components/single-select-with-arrows";
import { ChartLayoutToggle, TChartLayoutMode } from "./components/chart-layout-toggle";
import { OpenableWindow } from "../../ui/openable-window";
import { TChartExportHandler, TChartExportFormat } from "./components/chart-export";
import { FaultFrequenciesPanel } from "./components/fault-frequencies-panel";
import { hasAnalyticsDisplayData, IAnalyticsDataAvailability } from "./utils/dataAvailability";
import { getMeasureSelectionKey } from "./components/single-select-with-arrows/utils";
import { useSubscribe } from "../../../../../subscribe/provider";
import { SubscribeCode, SubscribeMessage } from "../../../../../subscribe/types";
import {
  StyledAnalyticsBottomControls,
  StyledAnalyticsContent,
  StyledAnalyticsControls,
  StyledAnalyticsRoot,
  StyledAnalyticsToolbar,
  StyledAnalyticsViewPanel,
} from "./styled";

type TSignalChartMeasureType = ANALYTICS_MEASURE_TYPE.SPECTRUM | ANALYTICS_MEASURE_TYPE.SIGNAL_FORM;

const DEFAULT_DISPLAY_CASCADE_3D_BY_MEASURE_TYPE: Record<TSignalChartMeasureType, boolean> = {
  [ANALYTICS_MEASURE_TYPE.SPECTRUM]: true,
  [ANALYTICS_MEASURE_TYPE.SIGNAL_FORM]: true,
};

const isSignalChartMeasureType = (measureType: ANALYTICS_MEASURE_TYPE): measureType is TSignalChartMeasureType =>
  measureType === ANALYTICS_MEASURE_TYPE.SPECTRUM || measureType === ANALYTICS_MEASURE_TYPE.SIGNAL_FORM;

interface IAnalyticsProps {
  isNarrow?: boolean;
}

export const Trends: FC<IAnalyticsProps> = ({ isNarrow }) => {
  const normalizeResponseArray = <T,>(value: T[] | null | undefined | void): T[] => (Array.isArray(value) ? value : []);

  const analyticsRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<UseFormReturn<FieldValues, any> | null>(null);
  const resetZoomChartsRef = useRef<(() => void) | null>(null);
  const exportChartsRef = useRef<TChartExportHandler | null>(null);
  const recorderModeContextKeyRef = useRef<string | null>(null);
  const analyticsMountGenerationRef = useRef(0);
  const analyticsHydratedGenerationRef = useRef(-1);
  const prevDashboardTabRef = useRef<string | null>(null);
  const hydrationRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const forceRecorderFullWindowFetchRef = useRef(false);
  const recorderTrendRequestIdRef = useRef(0);
  const lastSuccessfulRecorderEndRef = useRef<string | null>(null);
  const pendingSchemeTabReloadRef = useRef(false);
  const skipAnalyticsRefreshAfterTabReturnRef = useRef(false);
  const preservedAnalyticsSnapshotRef = useRef<{
    chartData: IChartData[] | null;
    tableData: ITableData | null;
    chartDataSignal: IChartDataSignal[] | null;
    chartDataOrbit: IChartDataOrbit[] | null;
  } | null>(null);
  const requestTimestampsRef = useRef<{ start: string | Date; end: string | Date }>({
    start: "",
    end: "",
  });
  const analyticsDisplayDataRef = useRef<IAnalyticsDataAvailability & { view: ANALYTICS_VIEW }>({
    view: ANALYTICS_VIEW.CHART,
    chartData: null,
    tableData: null,
    chartDataSignal: null,
    chartDataOrbit: null,
  });

  const [chartData, setChartData] = useState<IChartData[] | null>(null);
  const [tableData, setTableData] = useState<ITableData | null>(null);
  const [chartDataSignal, setChartDataSignal] = useState<IChartDataSignal[] | null>(null);
  const [chartDataOrbit, setChartDataOrbit] = useState<IChartDataOrbit[] | null>(null);
  const [trainRotationDirection, setTrainRotationDirection] = useState<TRAIN_ROTATION_DIRECTION | null>(null);
  const [isPolarTrendChecked, setIsPolarTrendChecked] = useState<boolean>(false);
  const [isCombinedAmpPhaseChecked, setIsCombinedAmpPhaseChecked] = useState<boolean>(false);
  const [isHarmonicCursorsChecked, setIsHarmonicCursorsChecked] = useState<boolean>(false);
  const [isSubHarmonicCursorsChecked, setIsSubHarmonicCursorsChecked] = useState<boolean>(false);
  const [displayCascade3DByMeasureType, setDisplayCascade3DByMeasureType] = useState<
    Record<TSignalChartMeasureType, boolean>
  >(DEFAULT_DISPLAY_CASCADE_3D_BY_MEASURE_TYPE);
  const [hasActiveZoom, setHasActiveZoom] = useState<boolean>(false);
  const [faultFrequencies, setFaultFrequencies] = useState<IComponentFaultFrequencyApi[] | null>(null);
  const [selectedFaultLineKeys, setSelectedFaultLineKeys] = useState<string[]>([]);
  const [isFaultFrequenciesPanelOpen, setIsFaultFrequenciesPanelOpen] = useState<boolean>(false);
  const [chartRenderKey, setChartRenderKey] = useState(0);
  const faultFrequenciesPanelOpenRef = useRef(false);
  const measureColorAssignerRef = useRef(createStableColorAssigner());
  const measureColorContextKeyRef = useRef("");

  const clearAnalyticsData = useCallback(() => {
    lastSuccessfulRecorderEndRef.current = null;
    setChartData(null);
    setTableData(null);
    setChartDataSignal(null);
    setChartDataOrbit(null);
    setTrainRotationDirection(null);
    setFaultFrequencies(null);
    setSelectedFaultLineKeys([]);
    setIsFaultFrequenciesPanelOpen(false);
  }, []);

  useEffect(() => {
    faultFrequenciesPanelOpenRef.current = isFaultFrequenciesPanelOpen;
  }, [isFaultFrequenciesPanelOpen]);

  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    isAmplitudePhaseSelected,
    userSelectedRangeType,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
    recorderWindowStart,
    recorderWindowEnd,
    isRecorderModeActive,
    tab,
    triggerRecorderReset,
    dbTz,
  } = useAsu();
  const isRecorderMode = userSelectedRangeType === RangeType.RECORDER_MODE;

  const dashboardTabRef = useRef(tab);
  dashboardTabRef.current = tab;

  const isSchemeDashboardTabActive = (): boolean => dashboardTabRef.current === "1";

  const { profile } = useUser<IUser>();
  const { connected: subscribeConnected, enabled: subscribeModeEnabled, subscribe, viewportEnd } = useSubscribe();
  const subscribeModeActive = subscribeModeEnabled && subscribeConnected;
  const hasAdvancedFeaturesAccess =
    profile.version === UserVersion.ADVANCED || profile.version === UserVersion.PROFESSIONAL;

  const measuresMapRef = useRef<{ [key: string]: number[] }>({});

  const settings = useSettings();
  const schemeState = settings.getSchemeState();
  const { analytics } = schemeState;

  const location = useLocation();
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  const measureTypeFromQuery = queryParams.measureType
    ? (Number(queryParams.measureType) as ANALYTICS_MEASURE_TYPE)
    : ANALYTICS_MEASURE_TYPE.TREND;
  const view = queryParams.view ? (Number(queryParams.view) as ANALYTICS_VIEW) : ANALYTICS_VIEW.CHART;

  const isTrendSelected = measureTypeFromQuery === ANALYTICS_MEASURE_TYPE.TREND;
  const isSignalChartSelected = isSignalChartMeasureType(measureTypeFromQuery);
  const isSpectrumOrSignalOrOrbitSelected =
    isSignalChartSelected || measureTypeFromQuery === ANALYTICS_MEASURE_TYPE.ORBIT;
  const isSpectrumSelected = measureTypeFromQuery === ANALYTICS_MEASURE_TYPE.SPECTRUM;
  const isSignalFormSelected = measureTypeFromQuery === ANALYTICS_MEASURE_TYPE.SIGNAL_FORM;

  const [searchParams, setSearchParams] = useSearchParams();
  const onlyPage = searchParams.get("onlyPage") === "true";
  const currentComponent = searchParams.get("component");
  const measureTypeQueryParam = searchParams.get("measureType");
  const objectQueryParam = searchParams.get("object");
  const idMeasureQueryParam = searchParams.get("idMeasure");

  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.scheme.analytics.title" });
  const noChartData = formatMessage({ id: "pages.scheme.analytics.noChartData" });
  const polarTrendLabel = formatMessage({ id: "pages.scheme.analytics.showPolarTrend" });
  const combinedAmpPhaseLabel = formatMessage({ id: "pages.scheme.analytics.combinedAmpPhase" });
  const harmonicCursorsLabel = "Гармонические курсоры";
  const subHarmonicCursorsLabel = "Субгармонические курсоры";
  const faultFrequenciesLabel = "Частоты неисправностей";
  const chartLayout2dLabel = formatMessage({ id: "pages.scheme.analytics.spectrumLayout2d" });
  const chartLayout3dLabel = formatMessage({ id: "pages.scheme.analytics.spectrumLayout3d" });
  const spectrumLayoutTooltip = formatMessage({ id: "pages.scheme.analytics.spectrumLayoutTooltip" });
  const signalLayoutTooltip = formatMessage({ id: "pages.scheme.analytics.signalLayoutTooltip" });
  const resetZoomLabel = formatMessage({ id: "pages.scheme.analytics.resetZoom" });

  const toFakeUtcISO = useToFakeUtcISO();

  const { enqueueSnackbar } = useSnackbar();

  const idTrain = useMemo(
    () => (selectedItem && "TrainId" in selectedItem && selectedItem.TrainId) || null,
    [selectedItem],
  );
  const trainName = useMemo(
    () => selectedItem && "TrainName" in selectedItem && "TrainId" in selectedItem && selectedItem.TrainName,
    [selectedItem],
  );

  const finalChartData = useMemo(() => chartData, [chartData]);
  const finalTableData = useMemo(() => tableData, [tableData]);
  const finalChartDataSignal = useMemo(() => chartDataSignal, [chartDataSignal]);

  const recorderTrendTimeBounds = useMemo(() => {
    if (!subscribeModeActive) {
      return null;
    }

    const dataEnd = toFakeUtc(viewportEnd, dbTz);
    const dataStart = calculateStartDate(
      dataEnd,
      profile.selfRecorderSettings.range,
      profile.selfRecorderSettings.value,
    );
    const dataDurationMs = dataEnd.getTime() - dataStart.getTime();

    if (!Number.isFinite(dataDurationMs) || dataDurationMs <= 0) {
      return null;
    }

    return {
      start: new Date(dataStart.getTime() - dataDurationMs * 0.05).toISOString(),
      end: new Date(dataEnd.getTime() + dataDurationMs / 9).toISOString(),
    };
  }, [dbTz, profile.selfRecorderSettings.range, profile.selfRecorderSettings.value, subscribeModeActive, viewportEnd]);

  const subscribedSignalMeasureIdsKey = useMemo(
    () =>
      Array.from(new Set((chartDataSignal ?? []).map(series => series.idMeasure).filter((id): id is number => !!id)))
        .sort((a, b) => a - b)
        .join(","),
    [chartDataSignal],
  );

  const subscribedParameterTrainIdsKey = useMemo(
    () =>
      Array.from(new Set((chartData ?? []).map(series => series.idTrain).filter((id): id is number => !!id)))
        .sort((a, b) => a - b)
        .join(","),
    [chartData],
  );

  useEffect(() => {
    if (!subscribeModeActive || !isTrendSelected) {
      return;
    }

    const handleMessages = (messages: SubscribeMessage[], nextViewportEnd: Date): void => {
      const end = toFakeUtc(nextViewportEnd, dbTz);
      const start = calculateStartDate(end, profile.selfRecorderSettings.range, profile.selfRecorderSettings.value);
      const startTime = start.getTime();

      setChartData(
        previous =>
          previous?.map(series => {
            const incoming = messages.filter(
              (message): message is Extract<SubscribeMessage, { idMeasure: number }> =>
                "idMeasure" in message &&
                !!measuresMapRef.current[series.parameterName ?? ""]?.some((id: number) => id === message.idMeasure),
            );
            const existingDates = new Set(series.items.map(item => item.date));
            const items = [...series.items];
            for (const message of incoming) {
              const useValue2 = series.isAmplitudePhase === true && series.unitCode === 133;
              const value = useValue2 ? message.Value2 : message.Value1;
              if (value !== null && !existingDates.has(message.Date)) {
                items.push({ date: message.Date, value });
                existingDates.add(message.Date);
              }
            }
            return {
              ...series,
              items: items
                .filter(item => new Date(item.date).getTime() >= startTime)
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
            };
          }) ?? null,
      );
    };

    const cleanups = Object.values(measuresMapRef.current)
      .flat()
      .flatMap(id => [subscribe({ id: Number(id), type: SubscribeCode.SCALAR }, handleMessages)]);
    return () => cleanups.forEach(cleanup => cleanup());
  }, [
    dbTz,
    profile.selfRecorderSettings.range,
    profile.selfRecorderSettings.value,
    subscribe,
    subscribeModeActive,
    measuresMapRef.current,
  ]);

  useEffect(() => {
    if (!subscribeModeActive || !subscribedSignalMeasureIdsKey || !isSignalChartSelected) {
      return;
    }

    const handleMessages = (messages: SubscribeMessage[]): void => {
      setChartDataSignal(
        previous =>
          previous?.map(series => {
            const message = [...messages]
              .reverse()
              .find(item => "idMeasure" in item && item.idMeasure === series.idMeasure && Array.isArray(item.data));
            if (!message || !("data" in message) || !message.data) {
              return series;
            }
            return {
              ...series,
              date: message.Date,
              value1: message.Value1 ?? undefined,
              value2: message.Value2,
              items: message.data.map((value, index) => ({ x: index, y: value })),
            };
          }) ?? null,
      );
    };

    const cleanups = subscribedSignalMeasureIdsKey
      .split(",")
      .map(id => subscribe({ id: Number(id), type: SubscribeCode.DYNAMIC_DATA }, handleMessages));
    return () => cleanups.forEach(cleanup => cleanup());
  }, [subscribe, subscribeModeActive, subscribedSignalMeasureIdsKey, isSignalChartSelected]);

  useEffect(() => {
    if (!subscribeModeActive || !subscribedParameterTrainIdsKey) {
      return;
    }

    const handleMessages = (messages: SubscribeMessage[], nextViewportEnd: Date): void => {
      const end = toFakeUtc(nextViewportEnd, dbTz);
      const start = calculateStartDate(end, profile.selfRecorderSettings.range, profile.selfRecorderSettings.value);
      const startTime = start.getTime();

      setChartData(
        previous =>
          previous?.map(series => {
            const incoming = messages.filter(
              message =>
                "idTrain" in message &&
                message.idTrain === series.idTrain &&
                message.Param === series.paramId &&
                message.Value !== undefined,
            );
            const existingDates = new Set(series.items.map(item => item.date));
            const items = [...series.items];
            for (const message of incoming) {
              if ("Value" in message && message.Value !== undefined && !existingDates.has(message.Date)) {
                items.push({ date: message.Date, value: message.Value });
                existingDates.add(message.Date);
              }
            }
            return {
              ...series,
              items: items
                .filter(item => new Date(item.date).getTime() >= startTime)
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
            };
          }) ?? null,
      );
    };

    const cleanups = subscribedParameterTrainIdsKey
      .split(",")
      .map(Number)
      .map(id => subscribe({ id, type: SubscribeCode.PARAM }, handleMessages));
    return () => cleanups.forEach(cleanup => cleanup());
  }, [
    dbTz,
    profile.selfRecorderSettings.range,
    profile.selfRecorderSettings.value,
    subscribe,
    subscribeModeActive,
    subscribedParameterTrainIdsKey,
  ]);

  const finalChartDataOrbit = useMemo(() => {
    if (chartDataOrbit && chartDataOrbit.length > 0) {
      return chartDataOrbit;
    }

    if (isPolarTrendChecked && finalChartData && finalChartData.length > 0) {
      return transformAmplitudePhaseToOrbit(finalChartData);
    }

    return null;
  }, [chartDataOrbit, isPolarTrendChecked, finalChartData]);

  const hasDataToDisplay = useMemo(
    () =>
      hasAnalyticsDisplayData(view, {
        chartData: finalChartData,
        tableData: finalTableData,
        chartDataSignal: finalChartDataSignal,
        chartDataOrbit: finalChartDataOrbit,
      }),
    [finalChartData, finalTableData, finalChartDataSignal, finalChartDataOrbit, view],
  );

  const showResetZoomButton = useMemo(() => {
    return view === ANALYTICS_VIEW.CHART && hasDataToDisplay;
  }, [hasDataToDisplay, view]);

  const showFaultFrequenciesSidePanel = isFaultFrequenciesPanelOpen && isSpectrumSelected && !!finalChartDataSignal;

  const shouldShowComponent = useMemo(() => {
    if (!onlyPage) return true;
    if (currentComponent) return currentComponent === "trends";
    return !!profile.interfaceSettings.showMeasureData;
  }, [onlyPage, currentComponent, profile.interfaceSettings.showMeasureData]);

  const defaultMeasureType = measureTypeQueryParam ? Number(measureTypeQueryParam) : 0;
  const defaultObject = objectQueryParam ? (Number(objectQueryParam) as ANALYTICS_OBJECT) : 0;
  const defaultIdObject = defaultObject === ANALYTICS_OBJECT.TRAIN ? idTrain : null;
  const defaultIdObjectOptions =
    defaultObject === ANALYTICS_OBJECT.TRAIN ? (idTrain && trainName ? [{ id: idTrain, title: trainName }] : []) : [];

  const initialValues: IAnalyticsForm = useMemo(
    () => ({
      measureType: defaultMeasureType,
      object: defaultObject,
      idObject: defaultIdObject,
      idParameter: null,
      idMeasure: null,
      idObjectsOptions: defaultIdObjectOptions,
      idParametersOptions: [],
      idMeasuresOptions: [],
    }),
    [defaultMeasureType, defaultObject, defaultIdObject, defaultIdObjectOptions],
  );

  const prepareTrendChartDates = (chart: IChartData[]) =>
    chart.map(s => ({
      ...s,
      items: s.items.map(it => ({ ...it, date: toFakeUtcISO(it.date) })),
    }));

  const prepareAnalyticsViewData = (result: { chart: IChartData[]; table: ITableData }) => ({
    chart: prepareTrendChartDates(result.chart),
    table: result.table,
  });

  const handlePolarTrendChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPolarTrendChecked(event.target.checked);
  };

  const handleCombinedAmpPhaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCombinedAmpPhaseChecked(event.target.checked);
  };

  const handleHarmonicCursorsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsHarmonicCursorsChecked(event.target.checked);
  };

  const handleSubHarmonicCursorsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSubHarmonicCursorsChecked(event.target.checked);
  };

  const handleChartLayoutChange = (value: TChartLayoutMode) => {
    if (isSignalChartSelected) {
      setDisplayCascade3DByMeasureType(prev => ({
        ...prev,
        [measureTypeFromQuery]: value === "3d",
      }));
    }
  };

  const handleRegisterZoomReset = useCallback((handler: (() => void) | null) => {
    resetZoomChartsRef.current = handler;
  }, []);

  const handleRegisterChartExport = useCallback((handler: TChartExportHandler | null) => {
    exportChartsRef.current = handler;
  }, []);

  const handleAnyZoomStateChange = useCallback((isZoomed: boolean) => {
    setHasActiveZoom(isZoomed);
  }, []);

  const handleResetZoom = useCallback(() => {
    resetZoomChartsRef.current?.();
  }, []);

  const handleChartExport = useCallback((format: TChartExportFormat) => {
    exportChartsRef.current?.(format);
  }, []);

  const setExpanded = (isExpanded: boolean): void => {
    const newSchemeState = {
      ...schemeState,
      analytics: isExpanded,
    };

    settings.setSchemeState(newSchemeState);
  };

  const { fn: getCommonParametersData, isLoading: isCommonParametersLoading } = useApiCall(
    (
      api,
      { name, type, url, data }: IGetCommonParamsDataValues,
    ): Promise<{ chart: IChartData[]; table: ITableData }> => {
      return (
        api.fetchJson({
          url,
          method: "GET",
          data,
        }) as Promise<TCommonParamsResponse[]>
      ).then((json: TCommonParamsResponse[]) => {
        if (!Array.isArray(json) || !json.length) {
          enqueueSnackbar(formatMessage({ id: "snackbar.parameterNotFound" }), { variant: "error" });
          return createEmptyAnalyticsResponse(type);
        }

        const { chart, table } = transformCommonParamsToChartData(json, name, type);
        return prepareAnalyticsViewData({ chart, table });
      });
    },
    { success: false },
  );

  const { fn: getCommonPointsData, isLoading: isCommonPointsDataLoading } = useApiCall(
    (
      api,
      { type, measureName, url, data: { startTimestamp, endTimestamp, ...idData } }: IGetCommonPointsDataValues,
      parameter?: IParameterOption,
    ): Promise<{ chart: IChartData[]; table: ITableData }> => {
      return (
        api.fetchJson({
          url,
          method: "GET",
          data: { ...idData },
        }) as Promise<IPointResponse[] | null | undefined>
      ).then((pointsResponse: IPointResponse[] | null | undefined) => {
        const points = normalizeResponseArray(pointsResponse);

        if (!points.length) {
          enqueueSnackbar(formatMessage({ id: "snackbar.measuresNotFound" }), { variant: "error" });
          return createEmptyAnalyticsResponse(type);
        }

        return (
          api.fetchJson({
            url: "/point/measure/data",
            method: "GET",
            data: {
              points: JSON.stringify(points.map(({ id }) => id)),
              measureName,
              startTimestamp,
              endTimestamp,
            },
          }) as Promise<IMeasuresResponse[] | null | undefined>
        ).then((json: IMeasuresResponse[] | null | undefined) => {
          const measures = normalizeResponseArray(json);

          if (!measures.length) {
            enqueueSnackbar(formatMessage({ id: "snackbar.measuresNotFound" }), { variant: "error" });
            return createEmptyAnalyticsResponse(type);
          }

          const { chart, table } = transformMeasuresDataMultiplePoints(
            measures,
            points,
            measureName,
            type,
            parameter?.useValue2 ?? false,
            parameter?.title,
          );

          const formattedChart = chart.map(cd => ({
            ...cd,
            name: `${cd.name}${
              cd.pointDirection ? ` ${formatMessage({ id: `enums.pointDirection.${cd.pointDirection}` })}` : ""
            }`,
            unitCode: cd.unitCode ?? null,
            units: formatMessage({ id: `enums.units.${cd.units}` }),
          }));

          const formattedTableItems = table.items.map(it => ({
            ...it,
            name: `${it.name}${
              it.pointDirection ? ` ${formatMessage({ id: `enums.pointDirection.${it.pointDirection}` })}` : ""
            }`,
          }));
          return prepareAnalyticsViewData({ chart: formattedChart, table: { ...table, items: formattedTableItems } });
        });
      });
    },
    { success: false },
  );

  const { fn: getPointMeasures, isLoading: isPointMeasuresLoading } = useApiCall(
    (
      api,
      { measureName, pointName, type, points, ...data }: IGetPointMeasuresDataValues,
      parameter?: IParameterOption,
    ): Promise<{ chart: IChartData[]; table: ITableData }> => {
      return (
        api.fetchJson({
          url: "/point/measure/data",
          method: "GET",
          data: {
            measureName,
            points: JSON.stringify(points),
            ...data,
          },
        }) as Promise<IMeasuresResponse[] | null | undefined>
      ).then((json: IMeasuresResponse[] | null | undefined) => {
        const measures = normalizeResponseArray(json);

        if (!measures.length) {
          enqueueSnackbar(formatMessage({ id: "snackbar.measuresNotFound" }), { variant: "error" });
          return createEmptyAnalyticsResponse(type);
        }

        const { chart, table } = transformMeasuresDataSinglePoint(
          measures,
          pointName,
          measureName,
          type,
          parameter?.useValue2 ?? false,
          parameter?.title,
        );

        const formattedChart = chart.map(cd => ({
          ...cd,
          unitCode: cd.unitCode ?? null,
          units: formatMessage({ id: `enums.units.${cd.units}` }),
        }));
        return prepareAnalyticsViewData({ chart: formattedChart, table });
      });
    },
    { success: false },
  );

  const { fn: getSignalData, isLoading: isSignalDataLoading } = useApiCall(
    (api, { measureType, pointName, measures }: IGetSignalDataValues): Promise<IChartDataSignal[]> => {
      const measuresIds = JSON.stringify(
        measures.map(m => ({
          idMeasure: m.idMeasure,
          date: m.date,
        })),
      );

      return (
        api.fetchJson({
          url: "/point-v2/measure/signals/data",
          method: "GET",
          data: {
            measures: measuresIds,
          },
        }) as Promise<ISignalDataResponse[] | null | undefined>
      ).then((json: ISignalDataResponse[] | null | undefined) => {
        const signals = normalizeResponseArray(json);

        if (!signals.length) {
          enqueueSnackbar(formatMessage({ id: "snackbar.measuresNotFound" }), { variant: "error" });
          return [];
        }

        const chart = signals
          .map(measure => transformSingleSignalToChart(measure, pointName, measureType))
          .map(signal => ({
            ...signal,
            unitsXCode: signal.unitsXCode ?? null,
            unitsYCode: signal.unitsYCode ?? null,
            unitsX: signal.unitsX ? formatMessage({ id: `enums.units.${signal.unitsX}` }) : "",
            unitsY: signal.unitsY ? formatMessage({ id: `enums.units.${signal.unitsY}` }) : "",
          }));

        return chart;
      });
    },
    { success: false },
  );

  const { fn: getOrbitData, isLoading: isOrbitDataLoading } = useApiCall(
    (api, { idMeasure, date }: IGetOrbitDataValues): Promise<IChartDataOrbit[]> => {
      return (
        api.fetchJson({
          url: "/point-v2/measure/orbit/data",
          method: "GET",
          data: {
            idMeasure,
            date,
          },
        }) as Promise<IOrbitDataResponse[] | null | undefined>
      ).then((json: IOrbitDataResponse[] | null | undefined) => {
        const measures = normalizeResponseArray(json);

        if (!measures.length) {
          enqueueSnackbar(formatMessage({ id: "snackbar.measuresNotFound" }), { variant: "error" });
          return [];
        }

        const chart = measures
          .map((measure, index) => transformOrbitDataToChart(measure, index))
          .map(orbit => ({
            ...orbit,
            unitCode: orbit.unitCode ?? null,
            units: formatMessage({ id: `enums.units.${orbit.units}` }),
          }));

        return chart;
      });
    },
    { success: false },
  );

  const { fn: getTrainRotationDirection, isLoading: isTrainRotationDirectionLoading } = useApiCall(
    (api, { idTrain }: { idTrain: number }): Promise<TRAIN_ROTATION_DIRECTION> => {
      return (
        api.fetchJson({
          url: `/trains/${idTrain}/details`,
          method: "GET",
        }) as Promise<ITrainDetailsRotationResponse[]>
      ).then((json: ITrainDetailsRotationResponse[]) => json[0].RotationDirection);
    },
    { success: false },
  );

  const { fn: getComponentFaultFrequencies, isLoading: isFaultFrequenciesLoading } = useApiCall(
    (
      api,
      params: { idTrainComponent: number; idMeasure: number; measureDate?: string },
    ): Promise<IComponentFaultFrequencyApi[]> => {
      return api.fetchJson({
        url: "/component/fault-frequencies",
        method: "GET",
        data: {
          idTrainComponent: params.idTrainComponent,
          idMeasure: params.idMeasure,
          ...(params.measureDate ? { measureDate: params.measureDate } : {}),
        },
      }) as Promise<IComponentFaultFrequencyApi[]>;
    },
    { success: false },
  );

  const { fn: getFaultFrequencyComponents, isLoading: isFaultFrequencyComponentsLoading } = useApiCall(
    (api, params: { idTrain: number }): Promise<IComponentAutocompleteResponse[]> =>
      api.fetchJson({
        url: "/component/autocomplete",
        method: "GET",
        data: { idTrain: params.idTrain },
      }) as Promise<IComponentAutocompleteResponse[]>,
    { success: false },
  );

  const selectedFaultFrequenciesValues = useMemo((): IFaultFrequencyChartCursor[] | undefined => {
    if (!faultFrequencies?.length || !selectedFaultLineKeys.length) {
      return undefined;
    }

    const colorMap = buildFaultFrequencyColorMap(faultFrequencies);
    const out: IFaultFrequencyChartCursor[] = [];

    const addGroup = (hzList: (number | undefined)[], color: string) => {
      for (const v of hzList) {
        if (typeof v === "number" && Number.isFinite(v) && v > 0) {
          out.push({ frequencyHz: v, color });
        }
      }
    };

    for (const f of faultFrequencies) {
      const parentKey = getFaultFrequencySelectionKey(f);
      if (selectedFaultLineKeys.includes(parentKey)) {
        const color = colorMap.get(parentKey)!;
        if (f.lines?.length) {
          addGroup(
            f.lines.map(ln => ln.frequencyHz),
            color,
          );
        } else {
          addGroup([f.frequencyHz], color);
        }
        continue;
      }
      if (f.lines?.length) {
        for (const ln of f.lines) {
          if (selectedFaultLineKeys.includes(ln.lineKey)) {
            addGroup([ln.frequencyHz], colorMap.get(ln.lineKey)!);
          }
        }
      } else if (f.freqCode && selectedFaultLineKeys.includes(f.freqCode)) {
        addGroup([f.frequencyHz], colorMap.get(String(f.freqCode))!);
      }
    }
    return out.length ? out : undefined;
  }, [faultFrequencies, selectedFaultLineKeys]);

  const faultLineColorsByKey = useMemo((): Record<string, string> => {
    if (!faultFrequencies?.length) {
      return {};
    }

    const colorMap = buildFaultFrequencyColorMap(faultFrequencies);
    const colors: Record<string, string> = {};

    for (const f of faultFrequencies) {
      if (f.isVisible === false) {
        continue;
      }

      const key = getFaultFrequencySelectionKey(f);
      const color = colorMap.get(key);
      if (color) {
        colors[key] = color;
      }
    }

    return colors;
  }, [faultFrequencies]);

  const buildAnalyticsContext = (values: IAnalyticsForm) => {
    const measureType = get(values, measureTypeName);
    const object = get(values, objectName);
    const idObject = get(values, idObjectName);
    const idObjectsOptions = get(values, idObjectsOptionsName);
    const idParameter = get(values, idParameterName);
    const idParametersOptions = get(values, idParametersOptionsName, []);
    const idMeasure = get(values, idMeasureName);
    const idMeasuresOptions = get(values, idMeasuresOptionsName);

    const normalizeToArray = <T,>(value: T | T[] | null | undefined): T[] =>
      value == null ? [] : Array.isArray(value) ? value : [value];

    const isSameId = (a: number | string, b: number | string) => String(a) === String(b);

    const selectedObject: IOption | null =
      idObject != null && Array.isArray(idObjectsOptions)
        ? idObjectsOptions.find(o => isSameId(o.id, idObject)) ?? null
        : null;

    const parameterIds = normalizeToArray(idParameter);

    const selectedParameters: IOption[] = Array.isArray(idParametersOptions)
      ? idParametersOptions.filter(p => parameterIds.some(id => isSameId(p.id, id)))
      : [];

    const measureIds = normalizeToArray(idMeasure);

    const selectedMeasurements: IOption[] = Array.isArray(idMeasuresOptions)
      ? idMeasuresOptions.filter(m => measureIds.some(id => isSameId(m.id, id)))
      : [];

    const measuresMap: { [key: string]: number[] } = {};

    normalizeToArray(idParameter).forEach(name => {
      measuresMap[name] = (idParametersOptions.find((o: IObjectOption) => o.id === name)?.ids ?? []) as number[];
    });

    return {
      measureType,
      object,
      idObject,
      idObjectsOptions,
      idParameter,
      idParametersOptions,
      idMeasure,
      idMeasuresOptions,
      selectedObject,
      selectedParameters,
      selectedMeasurements,
      measuresMap,

      isTrend: measureType === ANALYTICS_MEASURE_TYPE.TREND,
      isSignal: measureType === ANALYTICS_MEASURE_TYPE.SIGNAL_FORM || measureType === ANALYTICS_MEASURE_TYPE.SPECTRUM,
      isOrbit: measureType === ANALYTICS_MEASURE_TYPE.ORBIT,

      isTrain: object === ANALYTICS_OBJECT.TRAIN,
      isMechanism: object === ANALYTICS_OBJECT.MECHANISM,
      isComponent: object === ANALYTICS_OBJECT.NODE,
      isPoint: object === ANALYTICS_OBJECT.POINT,
    };
  };

  const normalizeRecorderModeList = (value: string | string[] | null | undefined): string =>
    value == null
      ? ""
      : Array.isArray(value)
        ? [...value]
            .map(item => String(item).trim())
            .sort()
            .join(",")
        : String(value)
            .split(",")
            .map(item => item.trim())
            .filter(Boolean)
            .sort()
            .join(",");

  const getRecorderModeContextKey = (ctx: ReturnType<typeof buildAnalyticsContext>): string | null => {
    if (!ctx.idParameter) {
      return null;
    }

    const measureType = String(ctx.measureType ?? "");
    const object = String(ctx.object ?? "");
    const idObject = String(ctx.idObject ?? "");
    const idParameter = normalizeRecorderModeList(ctx.idParameter);
    const idMeasure = normalizeRecorderModeList(ctx.idMeasure);

    return `${measureType}|${object}|${idObject}|${idParameter}|${idMeasure}`;
  };

  const loadFaultFrequenciesData = useCallback(
    async (form: UseFormReturn<FieldValues, any>, options?: { warnOnBlock?: boolean }) => {
      const warn = options?.warnOnBlock !== false;
      const ctx = buildAnalyticsContext(form.getValues() as IAnalyticsForm);

      if (idTrain == null) {
        setFaultFrequencies([]);
        setIsFaultFrequenciesPanelOpen(false);
        return;
      }

      const idMeasureFromCtx =
        ctx.selectedMeasurements.length > 0 ? Number(ctx.selectedMeasurements[0].idMeasure) : Number.NaN;
      const idMeasureFromUrl = idMeasureQueryParam ? Number(idMeasureQueryParam) : Number.NaN;
      const idMeasure = Number.isFinite(idMeasureFromCtx) && idMeasureFromCtx > 0 ? idMeasureFromCtx : idMeasureFromUrl;

      if (!Number.isFinite(idMeasure) || idMeasure <= 0) {
        if (warn) {
          enqueueSnackbar("Для частот неисправностей не найден идентификатор измерения (idMeasure).", {
            variant: "warning",
          });
        }
        setFaultFrequencies([]);
        setIsFaultFrequenciesPanelOpen(false);
        return;
      }

      const measureDate = ctx.selectedMeasurements[0]?.measureDate;

      try {
        const componentsResponse = await getFaultFrequencyComponents(form, { idTrain });
        const components = Array.isArray(componentsResponse) ? componentsResponse : [];
        const componentFrequencyGroups = await Promise.all(
          components.map(async component => {
            try {
              const data = await getComponentFaultFrequencies(form, {
                idTrainComponent: Number(component.id),
                idMeasure,
                ...(measureDate ? { measureDate } : {}),
              });

              const mechanismName =
                component.MechanismName || (component as { mechanismName?: string }).mechanismName || "";

              return (Array.isArray(data) ? data : []).map(item => ({
                ...item,
                idTrainComponent: Number(component.id),
                idTrainMechanism: component.idTrainMechanism ?? null,
                trainComponentName: component.title,
                mechanismName,
              }));
            } catch {
              return [];
            }
          }),
        );
        const items = componentFrequencyGroups.flat();

        if (!items.length) {
          setFaultFrequencies([]);
          setSelectedFaultLineKeys([]);
          if (warn) {
            enqueueSnackbar("Для выбранного замера частоты неисправностей не найдены.", { variant: "warning" });
          }
          setIsFaultFrequenciesPanelOpen(false);
          return;
        }

        setFaultFrequencies(items);
        setSelectedFaultLineKeys(prev => filterValidFaultLineKeys(prev, items));
        setIsFaultFrequenciesPanelOpen(true);
      } catch {
        setFaultFrequencies([]);
        if (warn) {
          enqueueSnackbar("Не удалось загрузить частоты неисправностей.", { variant: "error" });
        }
        setIsFaultFrequenciesPanelOpen(false);
      }
    },
    [enqueueSnackbar, getComponentFaultFrequencies, getFaultFrequencyComponents, idMeasureQueryParam, idTrain],
  );

  const handleFaultFrequenciesToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsFaultFrequenciesPanelOpen(checked);
    if (!checked) {
      setSelectedFaultLineKeys([]);
      return;
    }

    const form = formRef.current;
    if (!form) {
      enqueueSnackbar("Форма аналитики не готова.", { variant: "warning" });
      setIsFaultFrequenciesPanelOpen(false);
      return;
    }

    await loadFaultFrequenciesData(form, { warnOnBlock: true });
  };

  const processRequestsNormalMode = async (
    requests: Promise<void | { chart: IChartData[]; table: ITableData }>[],
  ): Promise<{ chart: IChartData[] | null; table: ITableData | null; failed: boolean }> => {
    const results = await Promise.all(requests);
    const failed = requests.length > 0 && results.some(result => result === undefined);

    const filteredResults = results.filter(Boolean) as Array<{ chart?: IChartData[]; table?: ITableData }>;
    const incomingCharts = filteredResults.flatMap(r => r.chart ?? []);
    const hasCharts = incomingCharts.length > 0;
    const incomingTables = filteredResults.flatMap(r => (r.table ? [r.table] : []));

    let combinedTable: ITableData | null = null;

    if (incomingTables.length === 1) {
      combinedTable = incomingTables[0];
    } else if (incomingTables.length > 1) {
      combinedTable = mergeRecorderModeTableDataMultiple(null, incomingTables);
    }

    return {
      chart: hasCharts ? incomingCharts : null,
      table: combinedTable,
      failed,
    };
  };

  const processRequestsRecorderMode = async (
    requests: Promise<void | { chart: IChartData[]; table: ITableData }>[],
  ): Promise<{ chart: IChartData[] | null; table: ITableData | null; failed: boolean }> => {
    const results = await Promise.all(requests);
    const failed = requests.length > 0 && results.some(result => result === undefined);
    const filteredResults = results.filter(Boolean) as Array<{ chart?: IChartData[]; table?: ITableData }>;
    const incomingCharts = filteredResults.flatMap(r => r.chart ?? []);
    const incomingTables = filteredResults.flatMap(r => (r.table ? [r.table] : []));
    const combinedIncomingTables = mergeRecorderModeTableDataMultiple(null, incomingTables);

    return {
      chart: incomingCharts.length ? incomingCharts : null,
      table: combinedIncomingTables,
      failed,
    };
  };

  const processRequests = async (
    requests: Promise<void | { chart: IChartData[]; table: ITableData }>[],
  ): Promise<{ chart: IChartData[] | null; table: ITableData | null; failed: boolean }> => {
    if (!requests.length) return { chart: null, table: null, failed: false };

    if (userSelectedRangeType !== RangeType.RECORDER_MODE) {
      return processRequestsNormalMode(requests);
    } else {
      return processRequestsRecorderMode(requests);
    }
  };

  const handleTrendSubmit = async (
    ctx: ReturnType<typeof buildAnalyticsContext>,
    form?: UseFormReturn<FieldValues, any>,
    requestRange?: { start: string | Date; end: string | Date },
  ) => {
    let combinedChartData: IChartData[] | null = null;
    let combinedTableData: ITableData | null = null;
    let fetchFailed = false;
    const trendRequestStartTimestamp = requestRange?.start ?? requestTimestampsRef.current.start;
    const trendRequestEndTimestamp = requestRange?.end ?? requestTimestampsRef.current.end;

    if (ctx.isTrain && ctx.idObject && ctx.idParameter) {
      const idTrain = ctx.idObject;

      const requests = ctx.selectedParameters.map(parameter => {
        if (parameter.group === PARAMETER_TYPE.parameter || parameter.group === PARAMETER_TYPE.keyphasor) {
          const idxKey = Number(String(parameter.id).split("_").slice(1)[0]);
          return getCommonParametersData(form, {
            name: parameter.title,
            type: parameter.group,
            url: `/trains/${idTrain}/param-data`,
            data: {
              idxKey,
              kind: parameter.group,
              startTimestamp: trendRequestStartTimestamp,
              endTimestamp: trendRequestEndTimestamp,
            },
          });
        } else {
          const measureName = parameter.id;
          return getCommonPointsData(
            form,
            {
              measureName,
              type: PARAMETER_TYPE.point,
              url: "/point/train/autocomplete",
              data: { idTrain, startTimestamp: trendRequestStartTimestamp, endTimestamp: trendRequestEndTimestamp },
            },
            parameter,
          );
        }
      });

      const { chart, table, failed } = await processRequests(requests);
      combinedChartData = chart;
      combinedTableData = table;
      fetchFailed = fetchFailed || failed;
    }

    if (ctx.isMechanism && ctx.idObject && ctx.idParameter) {
      const idTrainMech = ctx.idObject;

      const requests = ctx.selectedParameters.map(parameter => {
        if (parameter.group === PARAMETER_TYPE.keyphasor) {
          const idxKey = Number(String(parameter.id).split("_").slice(1)[0]);
          return getCommonParametersData(form, {
            name: parameter.title,
            type: parameter.group,
            url: "/mechanism/keyphasor/data",
            data: {
              idTrainMech,
              idxKey,
              startTimestamp: trendRequestStartTimestamp,
              endTimestamp: trendRequestEndTimestamp,
            },
          });
        } else if (parameter.group === PARAMETER_TYPE.parameter) {
          const idxKey = Number(String(parameter.id).split("_").slice(1)[0]);
          return getCommonParametersData(form, {
            name: parameter.title,
            type: parameter.group,
            url: "/mechanism/parameter/data",
            data: {
              idTrainMech,
              idxKey,
              startTimestamp: trendRequestStartTimestamp,
              endTimestamp: trendRequestEndTimestamp,
            },
          });
        } else {
          const measureName = parameter.id;
          return getCommonPointsData(
            form,
            {
              measureName,
              type: PARAMETER_TYPE.point,
              url: "/point/mechanism/autocomplete",
              data: { idTrainMech, startTimestamp: trendRequestStartTimestamp, endTimestamp: trendRequestEndTimestamp },
            },
            parameter,
          );
        }
      });

      const { chart, table, failed } = await processRequests(requests);
      combinedChartData = chart;
      combinedTableData = table;
      fetchFailed = fetchFailed || failed;
    }

    if (ctx.isComponent && ctx.idObject && ctx.idParameter) {
      const idTrainComponent = ctx.idObject;

      const requests = ctx.selectedParameters.map(parameter => {
        if (parameter.group === PARAMETER_TYPE.keyphasor) {
          const idxKey = Number(String(parameter.id).split("_").slice(1)[0]);
          return getCommonParametersData(form, {
            name: parameter.title,
            type: parameter.group,
            url: "/component/keyphasor/data",
            data: {
              idTrainComponent,
              idxKey,
              startTimestamp: trendRequestStartTimestamp,
              endTimestamp: trendRequestEndTimestamp,
            },
          });
        } else if (parameter.group === PARAMETER_TYPE.parameter) {
          const idxKey = Number(String(parameter.id).split("_").slice(1)[0]);
          return getCommonParametersData(form, {
            name: parameter.title,
            type: parameter.group,
            url: "/component/parameter/data",
            data: {
              idTrainComponent,
              idxKey,
              startTimestamp: trendRequestStartTimestamp,
              endTimestamp: trendRequestEndTimestamp,
            },
          });
        } else {
          const measureName = parameter.id;
          return getCommonPointsData(
            form,
            {
              measureName,
              type: PARAMETER_TYPE.point,
              url: "/point/component/autocomplete",
              data: {
                idTrainComponent,
                startTimestamp: trendRequestStartTimestamp,
                endTimestamp: trendRequestEndTimestamp,
              },
            },
            parameter,
          );
        }
      });

      const { chart, table, failed } = await processRequests(requests);
      combinedChartData = chart;
      combinedTableData = table;
      fetchFailed = fetchFailed || failed;
    }

    if (ctx.isPoint && ctx.idObject && ctx.idParameter) {
      const requests = ctx.selectedParameters.map(parameter => {
        const points = [ctx.idObject];
        const pointName = ctx.idObjectsOptions.find((o: IOption) => o.id === ctx.idObject)?.title;

        return getPointMeasures(
          form,
          {
            pointName,
            points,
            measureName: parameter.id,
            type: PARAMETER_TYPE.point,
            startTimestamp: trendRequestStartTimestamp,
            endTimestamp: trendRequestEndTimestamp,
          },
          parameter,
        );
      });

      const { chart, table, failed } = await processRequests(requests);
      combinedChartData = chart;
      combinedTableData = table;
      fetchFailed = fetchFailed || failed;
    }

    return { combinedChartData, combinedTableData, failed: fetchFailed };
  };

  const handleSignalSubmit = async (
    ctx: ReturnType<typeof buildAnalyticsContext>,
    form?: UseFormReturn<FieldValues, any>,
  ) => {
    let combinedChartDataSignal: IChartDataSignal[] = [];

    if (ctx.idObject && ctx.idParameter && ctx.idMeasure !== null && ctx.selectedMeasurements.length > 0) {
      const pointName = ctx.idObjectsOptions.find((o: IOption) => o.id === ctx.idObject)?.title;

      const measureContextKey = `${ctx.measureType}|${ctx.idObject}|${ctx.idParameter}`;
      if (measureColorContextKeyRef.current !== measureContextKey) {
        measureColorAssignerRef.current.reset();
        measureColorContextKeyRef.current = measureContextKey;
      }

      const measureColorAssigner = measureColorAssignerRef.current;
      for (const measurement of ctx.selectedMeasurements) {
        const selectionKey = getMeasureSelectionKey(measurement);
        if (selectionKey) {
          measureColorAssigner.get(selectionKey);
        }
      }

      const measures = ctx.selectedMeasurements.map(m => ({
        idMeasure: m.idMeasure,
        date: m.measureDate,
      }));

      const result = await getSignalData(form, {
        measureType: ctx.measureType,
        pointName: pointName || "",
        measures,
      });
      combinedChartDataSignal = (result || []).map(signal => {
        const selectionKey = getMeasureSelectionKey({
          idMeasure: signal.idMeasure,
          measureDate: signal.date,
        });

        return {
          ...signal,
          color: measureColorAssigner.get(selectionKey ?? `${signal.idMeasure}|${signal.date}`),
        };
      });
    }

    return combinedChartDataSignal;
  };

  const handleOrbitSubmit = async (
    ctx: ReturnType<typeof buildAnalyticsContext>,
    form?: UseFormReturn<FieldValues, any>,
  ) => {
    let combinedChartDataOrbit: IChartDataOrbit[] = [];
    let loadedTrainRotationDirection: TRAIN_ROTATION_DIRECTION | null = null;

    if (ctx.idObject && ctx.idParameter && ctx.idMeasure !== null && ctx.selectedMeasurements.length > 0) {
      const requests = ctx.selectedMeasurements.map(measurement => {
        return getOrbitData(form, {
          idMeasure: measurement.idMeasure,
          date: measurement.measureDate,
        });
      });

      const rotationDirectionRequest = idTrain
        ? getTrainRotationDirection(form, { idTrain })
        : Promise.resolve(TRAIN_ROTATION_DIRECTION.UNDEFINED);
      const [rotationDirection, results] = await Promise.all([rotationDirectionRequest, Promise.all(requests)]);
      loadedTrainRotationDirection = rotationDirection ?? TRAIN_ROTATION_DIRECTION.UNDEFINED;
      combinedChartDataOrbit = results.flatMap(result => normalizeResponseArray(result));
    }

    return { chartDataOrbit: combinedChartDataOrbit, trainRotationDirection: loadedTrainRotationDirection };
  };

  const submitAnalyticsContext = async (
    ctx: ReturnType<typeof buildAnalyticsContext>,
    form?: UseFormReturn<FieldValues, any>,
  ): Promise<void> => {
    measuresMapRef.current = ctx.measuresMap;
    const currentRecorderModeContextKey = getRecorderModeContextKey(ctx);
    const isSameRecorderContext =
      currentRecorderModeContextKey !== null && currentRecorderModeContextKey === recorderModeContextKeyRef.current;

    if (userSelectedRangeType === RangeType.RECORDER_MODE && !isSameRecorderContext) {
      forceRecorderFullWindowFetchRef.current = true;
      lastSuccessfulRecorderEndRef.current = null;
    }

    const shouldUseRecorderFullWindow =
      userSelectedRangeType === RangeType.RECORDER_MODE &&
      forceRecorderFullWindowFetchRef.current &&
      Boolean(recorderWindowStart) &&
      Boolean(recorderWindowEnd);

    const recorderUpdateMs = Math.max(1, Number(profile.selfRecorderSettings?.upd) || 1) * 1000;
    const lastSuccessfulEnd = lastSuccessfulRecorderEndRef.current;
    const lastSuccessfulEndMs = lastSuccessfulEnd ? new Date(lastSuccessfulEnd).getTime() : NaN;
    const useSuccessfulDelta =
      userSelectedRangeType === RangeType.RECORDER_MODE &&
      !subscribeModeActive &&
      !shouldUseRecorderFullWindow &&
      Boolean(recorderWindowStart) &&
      Number.isFinite(lastSuccessfulEndMs);

    const requestRange = shouldUseRecorderFullWindow
      ? { start: recorderWindowStart, end: recorderWindowEnd }
      : useSuccessfulDelta
        ? {
            start: new Date(
              Math.max(new Date(recorderWindowStart).getTime(), lastSuccessfulEndMs - recorderUpdateMs),
            ).toISOString(),
            end: endTimestamp,
          }
        : { start: startTimestamp, end: endTimestamp };
    requestTimestampsRef.current = requestRange;

    if (userSelectedRangeType === RangeType.RECORDER_MODE && (!isRecorderModeActive || !recorderWindowStart)) {
      return;
    }

    if (!ctx.idParameter) {
      if (userSelectedRangeType !== RangeType.RECORDER_MODE) {
        setChartData(null);
        setTableData(null);
        setChartDataSignal(null);
        setChartDataOrbit(null);
        setTrainRotationDirection(null);
      }
      return;
    }

    if (ctx.isTrend) {
      const requestId = ++recorderTrendRequestIdRef.current;
      const { combinedChartData, combinedTableData, failed } = await handleTrendSubmit(ctx, form, requestRange);

      if (!isSchemeDashboardTabActive()) {
        return;
      }

      if (shouldUseRecorderFullWindow && requestId !== recorderTrendRequestIdRef.current) {
        return;
      }

      if (userSelectedRangeType === RangeType.RECORDER_MODE && !failed) {
        lastSuccessfulRecorderEndRef.current =
          typeof requestRange.end === "string" ? requestRange.end : new Date(requestRange.end).toISOString();
      }

      if (userSelectedRangeType === RangeType.RECORDER_MODE) {
        recorderModeContextKeyRef.current = currentRecorderModeContextKey;

        if (shouldUseRecorderFullWindow) {
          const windowed = (combinedChartData ?? [])
            .map(s => {
              const items = trimChartToWindow(s.items, recorderWindowStart);
              return items.length ? { ...s, items } : null;
            })
            .filter(Boolean) as IChartData[];
          setChartData(windowed.length ? windowed : null);
          setTableData(trimTableToWindow(combinedTableData, recorderWindowStart));
        } else {
          setChartData(prev => {
            const baseChartData = isSameRecorderContext ? prev ?? [] : [];
            const merged = mergeRecorderModeChartData(baseChartData, combinedChartData ?? []);
            const windowed = merged
              .map(s => {
                const items = trimChartToWindow(s.items, recorderWindowStart);
                return items.length ? { ...s, items } : null;
              })
              .filter(Boolean) as IChartData[];
            return windowed.length ? windowed : null;
          });
          setTableData(prev => {
            const baseTableData = isSameRecorderContext ? prev : null;
            const merged = mergeRecorderModeTableDataMultiple(
              baseTableData,
              combinedTableData ? [combinedTableData] : [],
            );
            return trimTableToWindow(merged, recorderWindowStart);
          });
        }

        forceRecorderFullWindowFetchRef.current = false;
      } else {
        setChartData(combinedChartData);
        setTableData(combinedTableData);
      }

      setChartDataSignal(null);
      setChartDataOrbit(null);
      setTrainRotationDirection(null);
      return;
    }

    if (ctx.isSignal && (ctx.isTrain || ctx.isComponent || ctx.isMechanism || ctx.isPoint)) {
      const combinedChartDataSignal = await handleSignalSubmit(ctx, form);

      if (!isSchemeDashboardTabActive()) {
        return;
      }

      setChartData(null);
      setTableData(null);
      setChartDataOrbit(null);
      setTrainRotationDirection(null);
      setFaultFrequencies(null);
      if (!faultFrequenciesPanelOpenRef.current) {
        setSelectedFaultLineKeys([]);
      }

      if (ctx.idMeasure == null || combinedChartDataSignal.length === 0) {
        setChartDataSignal(null);
        setIsFaultFrequenciesPanelOpen(false);
      } else {
        setChartDataSignal(combinedChartDataSignal);
        if (faultFrequenciesPanelOpenRef.current && form) {
          await loadFaultFrequenciesData(form, { warnOnBlock: false });
        } else {
          setIsFaultFrequenciesPanelOpen(false);
        }
      }

      return;
    }

    if (ctx.isOrbit && (ctx.isTrain || ctx.isComponent || ctx.isMechanism || ctx.isPoint)) {
      const { chartDataOrbit: combinedChartDataOrbit, trainRotationDirection: loadedTrainRotationDirection } =
        await handleOrbitSubmit(ctx, form);

      if (!isSchemeDashboardTabActive()) {
        return;
      }

      if (ctx.idMeasure == null || combinedChartDataOrbit.length === 0) {
        setChartDataOrbit(null);
        setTrainRotationDirection(null);
      } else {
        setTrainRotationDirection(loadedTrainRotationDirection);
        setChartDataOrbit(combinedChartDataOrbit);
      }

      setChartData(null);
      setTableData(null);
      setChartDataSignal(null);
    }
  };

  const handleSubmit = async (values: IAnalyticsForm, form: UseFormReturn<FieldValues, any>): Promise<void> => {
    formRef.current = form;
    setIsPolarTrendChecked(false);

    const ctx = buildAnalyticsContext(values);
    await submitAnalyticsContext(ctx, form);
  };

  const refreshAnalyticsIfReady = (form: UseFormReturn<FieldValues, any>): void => {
    if (tab !== "1") {
      return;
    }

    const values = form.getValues() as IAnalyticsForm;
    const ctx = buildAnalyticsContext(values);

    if (!ctx.idParameter) {
      return;
    }

    void submitAnalyticsContext(ctx, form);
  };

  analyticsDisplayDataRef.current = {
    view,
    chartData,
    tableData,
    chartDataSignal,
    chartDataOrbit,
  };

  const hasDisplayableAnalyticsData = (): boolean =>
    hasAnalyticsDisplayData(analyticsDisplayDataRef.current.view, analyticsDisplayDataRef.current);

  const preserveAnalyticsSnapshot = (): void => {
    if (!hasDisplayableAnalyticsData()) {
      return;
    }

    preservedAnalyticsSnapshotRef.current = {
      chartData,
      tableData,
      chartDataSignal,
      chartDataOrbit,
    };
  };

  const restoreAnalyticsSnapshot = (): boolean => {
    const snapshot = preservedAnalyticsSnapshotRef.current;
    if (!snapshot) {
      return false;
    }

    setChartData(snapshot.chartData);
    setTableData(snapshot.tableData);
    setChartDataSignal(snapshot.chartDataSignal);
    setChartDataOrbit(snapshot.chartDataOrbit);
    preservedAnalyticsSnapshotRef.current = null;
    analyticsHydratedGenerationRef.current = analyticsMountGenerationRef.current;
    return true;
  };

  const clearAnalyticsHydrationRetries = (): void => {
    if (hydrationRetryTimeoutRef.current !== null) {
      clearTimeout(hydrationRetryTimeoutRef.current);
      hydrationRetryTimeoutRef.current = null;
    }
  };

  const tryHydrateAnalyticsFromForm = async (form: UseFormReturn<FieldValues, any>): Promise<boolean> => {
    if (userSelectedRangeType !== RangeType.RECORDER_MODE) {
      return hasDisplayableAnalyticsData();
    }

    if (hasDisplayableAnalyticsData()) {
      analyticsHydratedGenerationRef.current = analyticsMountGenerationRef.current;
      return true;
    }

    if (analyticsHydratedGenerationRef.current === analyticsMountGenerationRef.current) {
      return true;
    }

    const values = form.getValues() as IAnalyticsForm;
    const ctx = buildAnalyticsContext(values);

    if (!ctx.idParameter) {
      return false;
    }

    if (!isRecorderModeActive || !recorderWindowStart) {
      return false;
    }

    forceRecorderFullWindowFetchRef.current = true;
    recorderModeContextKeyRef.current = null;

    await submitAnalyticsContext(ctx, form);
    analyticsHydratedGenerationRef.current = analyticsMountGenerationRef.current;
    return true;
  };

  const scheduleAnalyticsHydrationRetries = (): void => {
    if (tab !== "1" || userSelectedRangeType !== RangeType.RECORDER_MODE) {
      return;
    }

    if (
      hasDisplayableAnalyticsData() ||
      analyticsHydratedGenerationRef.current === analyticsMountGenerationRef.current
    ) {
      return;
    }

    clearAnalyticsHydrationRetries();

    let attempts = 0;
    const maxAttempts = 40;

    const tryOnce = (): void => {
      const form = formRef.current;

      if (!form) {
        attempts += 1;
        if (attempts < maxAttempts) {
          hydrationRetryTimeoutRef.current = setTimeout(tryOnce, 250);
        }
        return;
      }

      void tryHydrateAnalyticsFromForm(form).then(wasAttempted => {
        if (wasAttempted) {
          clearAnalyticsHydrationRetries();
          return;
        }

        attempts += 1;
        if (attempts < maxAttempts) {
          hydrationRetryTimeoutRef.current = setTimeout(tryOnce, 250);
        }
      });
    };

    tryOnce();
  };

  const handleFormStateChange = (form: UseFormReturn<FieldValues, any>): Promise<void> => {
    formRef.current = form;

    if (
      userSelectedRangeType === RangeType.RECORDER_MODE &&
      analyticsHydratedGenerationRef.current !== analyticsMountGenerationRef.current
    ) {
      scheduleAnalyticsHydrationRetries();
    }

    return Promise.resolve();
  };

  useEffect(() => {
    analyticsMountGenerationRef.current += 1;
    analyticsHydratedGenerationRef.current = -1;

    if (userSelectedRangeType === RangeType.RECORDER_MODE) {
      scheduleAnalyticsHydrationRetries();
    }

    return () => {
      clearAnalyticsHydrationRetries();
    };
  }, []);

  useEffect(() => {
    if (tab !== "1") {
      if (prevDashboardTabRef.current === "1") {
        preserveAnalyticsSnapshot();
      }

      prevDashboardTabRef.current = tab;
      return;
    }

    const returnedFromOtherTab = prevDashboardTabRef.current !== null && prevDashboardTabRef.current !== "1";
    prevDashboardTabRef.current = tab;

    if (!returnedFromOtherTab) {
      return;
    }

    const restoredFromSnapshot = restoreAnalyticsSnapshot();

    setChartRenderKey(previous => previous + 1);

    requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });

    if (restoredFromSnapshot || hasDisplayableAnalyticsData()) {
      skipAnalyticsRefreshAfterTabReturnRef.current = true;
      return;
    }

    analyticsHydratedGenerationRef.current = -1;
    pendingSchemeTabReloadRef.current = true;

    if (userSelectedRangeType === RangeType.RECORDER_MODE) {
      forceRecorderFullWindowFetchRef.current = true;
      recorderModeContextKeyRef.current = null;
      triggerRecorderReset();
      scheduleAnalyticsHydrationRetries();
    }

    const form = formRef.current;
    if (form) {
      refreshAnalyticsIfReady(form);
    }
  }, [tab, chartData, tableData, chartDataSignal, chartDataOrbit, view, userSelectedRangeType]);

  useEffect(() => {
    if (tab !== "1" || !pendingSchemeTabReloadRef.current) {
      return;
    }

    if (userSelectedRangeType === RangeType.RECORDER_MODE && (!isRecorderModeActive || !recorderWindowStart)) {
      return;
    }

    if (hasDisplayableAnalyticsData()) {
      pendingSchemeTabReloadRef.current = false;
      return;
    }

    pendingSchemeTabReloadRef.current = false;

    if (userSelectedRangeType === RangeType.RECORDER_MODE) {
      forceRecorderFullWindowFetchRef.current = true;
      recorderModeContextKeyRef.current = null;
    }

    const form = formRef.current;
    if (form) {
      refreshAnalyticsIfReady(form);
    } else {
      scheduleAnalyticsHydrationRetries();
    }
  }, [
    tab,
    recorderWindowStart,
    isRecorderModeActive,
    userSelectedRangeType,
    chartData,
    tableData,
    chartDataSignal,
    chartDataOrbit,
    view,
  ]);

  useEffect(() => {
    if (tab !== "1") {
      return;
    }

    if (subscribeModeActive && analyticsDisplayDataRef.current.chartData) {
      return;
    }

    if (skipAnalyticsRefreshAfterTabReturnRef.current) {
      skipAnalyticsRefreshAfterTabReturnRef.current = false;
      return;
    }

    const form = formRef.current;
    if (!form) {
      return;
    }

    refreshAnalyticsIfReady(form);
  }, [tab, startTimestamp, endTimestamp, recorderWindowStart, subscribeModeActive]);

  useEffect(() => {
    const currentSearchParams = parse(location.search.replace("?", ""));
    const scrollToAnalyticsParams = currentSearchParams.scrollToAnalytics;

    if (scrollToAnalyticsParams) {
      searchParams.delete("scrollToAnalytics");
      setSearchParams(searchParams);
      analyticsRef.current?.scrollIntoView({ behavior: "smooth" });
      setExpanded(true);
    }
  }, [location.search, searchParams.get("scrollToAnalytics")]);

  useEffect(() => {
    analyticsHydratedGenerationRef.current = -1;
    recorderModeContextKeyRef.current = null;
    clearAnalyticsData();
  }, [idTrain, isRecorderMode, recorderModeInterval, userSelectedRecorderModePeriod, clearAnalyticsData]);

  useEffect(() => {
    if (isRecorderMode) {
      return;
    }
    preservedAnalyticsSnapshotRef.current = null;
    clearAnalyticsData();
  }, [startTimestamp, endTimestamp, isRecorderMode, clearAnalyticsData]);

  const fileName = `train-scheme-trends-report-${new Date().toISOString().split("T")[0]}.csv`;
  const isLoading =
    isPointMeasuresLoading ||
    isCommonParametersLoading ||
    isCommonPointsDataLoading ||
    isSignalDataLoading ||
    isOrbitDataLoading ||
    isTrainRotationDirectionLoading;
  const hasMultipleSignalChartSeries = !!finalChartDataSignal && finalChartDataSignal.length > 1;
  const showChartLayoutToggle = view === ANALYTICS_VIEW.CHART && isSignalChartSelected && hasMultipleSignalChartSeries;
  const chartLayoutTooltip = isSignalFormSelected ? signalLayoutTooltip : spectrumLayoutTooltip;
  const chartDisplayCascade3D = isSignalChartSelected
    ? displayCascade3DByMeasureType[measureTypeFromQuery]
    : DEFAULT_DISPLAY_CASCADE_3D_BY_MEASURE_TYPE[ANALYTICS_MEASURE_TYPE.SPECTRUM];

  if (!shouldShowComponent) {
    return null;
  }

  const content = (
    <FormWrapper
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onFormStateChange={handleFormStateChange}
      showButtons={false}
      showPrompt={false}
    >
      <StyledAnalyticsRoot ref={analyticsRef}>
        <StyledAnalyticsToolbar>
          <Box
            sx={{
              flexShrink: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <AnalyticsViewSwitcher />
          </Box>
          {/* Чекбокс для полярного тренда (advanced и professional) */}
          {hasAdvancedFeaturesAccess &&
            view === ANALYTICS_VIEW.CHART &&
            isTrendSelected &&
            (finalChartData || finalChartDataOrbit) &&
            isAmplitudePhaseSelected && (
              <Box sx={{ flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPolarTrendChecked}
                      disabled={!isAmplitudePhaseSelected}
                      onChange={handlePolarTrendChange}
                      size="small"
                    />
                  }
                  label={
                    <Tooltip title={polarTrendLabel} arrow>
                      <Box
                        sx={{
                          fontSize: 12,
                          minWidth: 0,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {polarTrendLabel}
                      </Box>
                    </Tooltip>
                  }
                  sx={{ mx: 2, mt: 0.5, minWidth: 0 }}
                />
              </Box>
            )}
          {/* Чекбокс для амплитуды/фазы */}
          {view === ANALYTICS_VIEW.CHART &&
            isTrendSelected &&
            finalChartData &&
            isAmplitudePhaseSelected &&
            !isPolarTrendChecked && (
              <Box sx={{ flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCombinedAmpPhaseChecked}
                      disabled={!isAmplitudePhaseSelected}
                      onChange={handleCombinedAmpPhaseChange}
                      size="small"
                    />
                  }
                  label={
                    <Tooltip title={combinedAmpPhaseLabel} arrow>
                      <Box
                        sx={{
                          fontSize: 12,
                          minWidth: 0,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {combinedAmpPhaseLabel}
                      </Box>
                    </Tooltip>
                  }
                  sx={{ mx: 2, mt: 0.5, minWidth: 0 }}
                />
              </Box>
            )}
          {/* Кнопка для гармонических курсоров */}
          {view === ANALYTICS_VIEW.CHART && isSpectrumSelected && (
            <Box sx={{ flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
              <FormControlLabel
                control={
                  <Checkbox checked={isHarmonicCursorsChecked} onChange={handleHarmonicCursorsChange} size="small" />
                }
                label={
                  <Tooltip title={harmonicCursorsLabel} arrow>
                    <Box
                      sx={{
                        fontSize: 12,
                        minWidth: 0,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {harmonicCursorsLabel}
                    </Box>
                  </Tooltip>
                }
                sx={{ mx: 2, mt: 0.5, minWidth: 0 }}
              />
            </Box>
          )}
          {/* Кнопка для субгармонических курсоров */}
          {view === ANALYTICS_VIEW.CHART && isSpectrumSelected && (
            <Box sx={{ flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSubHarmonicCursorsChecked}
                    onChange={handleSubHarmonicCursorsChange}
                    size="small"
                  />
                }
                label={
                  <Tooltip title={subHarmonicCursorsLabel} arrow>
                    <Box
                      sx={{
                        fontSize: 12,
                        minWidth: 0,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {subHarmonicCursorsLabel}
                    </Box>
                  </Tooltip>
                }
                sx={{ mx: 2, mt: 0.5, minWidth: 0 }}
              />
            </Box>
          )}
          {view === ANALYTICS_VIEW.CHART && isSpectrumSelected && finalChartDataSignal && (
            <Box sx={{ flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFaultFrequenciesPanelOpen}
                    onChange={event => {
                      void handleFaultFrequenciesToggle(event);
                    }}
                    size="small"
                  />
                }
                label={
                  <Tooltip title={faultFrequenciesLabel} arrow>
                    <Box
                      sx={{
                        fontSize: 12,
                        minWidth: 0,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {faultFrequenciesLabel}
                    </Box>
                  </Tooltip>
                }
                sx={{ mx: 2, mt: 0.5, minWidth: 0 }}
              />
            </Box>
          )}
          {showChartLayoutToggle && (
            <ChartLayoutToggle
              mode={chartDisplayCascade3D ? "3d" : "2d"}
              tooltip={chartLayoutTooltip}
              layout2dLabel={chartLayout2dLabel}
              layout3dLabel={chartLayout3dLabel}
              onChange={handleChartLayoutChange}
            />
          )}
          {showResetZoomButton && (
            <Box
              sx={{
                flexShrink: 0,
                minWidth: "fit-content",
                mr: 1,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetZoom}
                disabled={!hasActiveZoom}
                sx={{
                  mt: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                {resetZoomLabel}
              </Button>
            </Box>
          )}
          <Box
            sx={{
              flexShrink: 0,
              minWidth: "fit-content",
            }}
          >
            <AnalyticsExport
              chartData={finalChartData}
              tableData={finalTableData}
              chartDataSignal={finalChartDataSignal}
              chartDataOrbit={finalChartDataOrbit}
              view={view}
              fileName={fileName}
              onDiagramExport={handleChartExport}
            />
          </Box>
        </StyledAnalyticsToolbar>

        <StyledAnalyticsControls>
          <Selects loading={isLoading} view={view} />
        </StyledAnalyticsControls>

        <StyledAnalyticsContent hasSidePanel={showFaultFrequenciesSidePanel}>
          <StyledAnalyticsViewPanel>
            {hasDataToDisplay ? (
              <AnalyticsView
                key={chartRenderKey}
                chartData={isPolarTrendChecked ? null : finalChartData}
                tableData={finalTableData}
                chartDataSignal={finalChartDataSignal}
                chartDataOrbit={finalChartDataOrbit}
                trendTimeBounds={recorderTrendTimeBounds}
                trainRotationDirection={trainRotationDirection}
                view={view}
                isPolarTrendChecked={isPolarTrendChecked}
                isCombinedAmpPhaseChecked={isCombinedAmpPhaseChecked}
                isHarmonicCursorsChecked={isHarmonicCursorsChecked}
                isSubHarmonicCursorsChecked={isSubHarmonicCursorsChecked}
                faultFrequencies={selectedFaultFrequenciesValues}
                spectrumDisplayCascade3D={displayCascade3DByMeasureType[ANALYTICS_MEASURE_TYPE.SPECTRUM]}
                signalDisplayCascade3D={displayCascade3DByMeasureType[ANALYTICS_MEASURE_TYPE.SIGNAL_FORM]}
                registerResetHandler={handleRegisterZoomReset}
                registerExportHandler={handleRegisterChartExport}
                onZoomStateChange={handleAnyZoomStateChange}
              />
            ) : (
              <EmptyPlaceholder icon={<AnalyticsIcon />} text={noChartData} />
            )}
          </StyledAnalyticsViewPanel>
          {showFaultFrequenciesSidePanel && (
            <FaultFrequenciesPanel
              items={faultFrequencies ?? []}
              selectedKeys={selectedFaultLineKeys}
              onChangeSelected={setSelectedFaultLineKeys}
              lineColorsByKey={faultLineColorsByKey}
              loading={isFaultFrequenciesLoading || isFaultFrequencyComponentsLoading}
              rootLabel={typeof trainName === "string" && trainName ? trainName : undefined}
            />
          )}
        </StyledAnalyticsContent>

        {isSpectrumOrSignalOrOrbitSelected && (
          <StyledAnalyticsBottomControls>
            <SingleSelectWithArrows loading={isLoading} />
          </StyledAnalyticsBottomControls>
        )}

        <AutoSave onSubmit={handleSubmit} />
      </StyledAnalyticsRoot>
    </FormWrapper>
  );

  return onlyPage && !!currentComponent ? (
    <OpenableWindow title={title} componentName="trends">
      {content}
    </OpenableWindow>
  ) : (
    <DashboardAccordion
      expanded={analytics}
      title={title}
      setExpanded={setExpanded}
      componentName="trends"
      isNarrow={isNarrow}
    >
      {content}
    </DashboardAccordion>
  );
};
